// ===============================================================
// V·IANet Governance -- Cross-Reference Popover
// Autocomplete search for document codes + names
// ===============================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';

interface DocumentSuggestion {
  id: string;
  codigo: string;
  titulo: string;
}

interface CrossRefPopoverProps {
  open: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  onSelect: (doc: { id: string; codigo: string; nombre: string }) => void;
}

export function CrossRefPopover({ open, anchorRect, onClose, onSelect }: CrossRefPopoverProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<DocumentSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSuggestions([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  // Debounced search
  const searchDocuments = useCallback(async (term: string) => {
    if (term.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const lang = localStorage.getItem('vianet-lang') || 'es';
      const res = await fetch(
        `/api/v1/documentos?search=${encodeURIComponent(term)}&porPagina=10&lang=${lang}`,
      );
      if (res.ok) {
        const data = await res.json();
        const docs = (data.datos ?? data.data ?? []) as DocumentSuggestion[];
        setSuggestions(docs);
        setSelectedIndex(0);
      }
    } catch {
      // Silently fail -- user can still type manually
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchDocuments(value), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && suggestions[selectedIndex]) {
        const doc = suggestions[selectedIndex];
        onSelect({ id: doc.id, codigo: doc.codigo, nombre: doc.titulo });
      } else if (query.trim()) {
        // Allow manual code entry even without matches
        onSelect({ id: '', codigo: query.trim(), nombre: '' });
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelectDoc = (doc: DocumentSuggestion) => {
    onSelect({ id: doc.id, codigo: doc.codigo, nombre: doc.titulo });
  };

  if (!open) return null;

  // Position below the anchor button
  const style: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10001,
    top: anchorRect ? anchorRect.bottom + 4 : 100,
    left: anchorRect ? anchorRect.left : 100,
    width: 360,
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
  };

  return (
    <div ref={popoverRef} style={style}>
      {/* Header */}
      <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F3864', marginBottom: 6 }}>
          {t.editor?.toolbar?.crossRef ?? 'Referencia cruzada'}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.editor?.crossRefPlaceholder ?? 'Buscar por codigo (ej: PE.21.NC)...'}
          style={{
            width: '100%',
            padding: '7px 10px',
            borderRadius: 6,
            border: '1px solid #E5E7EB',
            fontSize: 13,
            fontFamily: 'inherit',
            outline: 'none',
            boxSizing: 'border-box',
            color: '#374151',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#33BCE1';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#E5E7EB';
          }}
        />
      </div>

      {/* Results */}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {loading && (
          <div
            style={{ padding: '12px 16px', fontSize: 12, color: '#6B7280', textAlign: 'center' }}
          >
            {t.editor?.processingDocx ?? 'Buscando...'}
          </div>
        )}

        {!loading && query.length >= 3 && suggestions.length === 0 && (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
              {t.editor?.crossRefNoResults ??
                'Sin resultados. Pulsa Enter para insertar manualmente.'}
            </div>
          </div>
        )}

        {suggestions.map((doc, idx) => (
          <div
            key={doc.id}
            onClick={() => handleSelectDoc(doc)}
            onMouseEnter={() => setSelectedIndex(idx)}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              background: idx === selectedIndex ? '#F0F4FF' : 'transparent',
              borderBottom: '1px solid #F9FAFB',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: '#2F5496' }}>{doc.codigo}</div>
            <div
              style={{
                fontSize: 11,
                color: '#6B7280',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {doc.titulo}
            </div>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div
        style={{
          padding: '6px 12px',
          borderTop: '1px solid #F3F4F6',
          fontSize: 10,
          color: '#9CA3AF',
          display: 'flex',
          gap: 12,
        }}
      >
        <span>↑↓ {t.editor?.crossRefNavigate ?? 'navegar'}</span>
        <span>Enter {t.editor?.crossRefSelect ?? 'seleccionar'}</span>
        <span>Esc {t.editor?.crossRefClose ?? 'cerrar'}</span>
      </div>
    </div>
  );
}
